import type { Platform } from '@/types';

interface PlatformConfig {
  name: string;
  patterns: RegExp[];
  icon: string;
  color: string;
}

export const platformConfigs: Record<Platform, PlatformConfig> = {
  // Music & Podcasts
  spotify: {
    name: 'Spotify',
    patterns: [/open\.spotify\.com/, /spotify\.link/],
    icon: 'spotify',
    color: '#1DB954',
  },
  'apple-music': {
    name: 'Apple Music',
    patterns: [/music\.apple\.com/, /itunes\.apple\.com.*music/],
    icon: 'apple',
    color: '#FA243C',
  },
  deezer: {
    name: 'Deezer',
    patterns: [/deezer\.com/, /deezer\.page\.link/],
    icon: 'music',
    color: '#FEAA2D',
  },
  soundcloud: {
    name: 'SoundCloud',
    patterns: [/soundcloud\.com/],
    icon: 'cloud',
    color: '#FF5500',
  },
  'amazon-music': {
    name: 'Amazon Music',
    patterns: [/music\.amazon\.com/],
    icon: 'music',
    color: '#00A8E1',
  },
  'apple-podcasts': {
    name: 'Apple Podcasts',
    patterns: [/podcasts\.apple\.com/],
    icon: 'mic',
    color: '#9933CC',
  },
  // youtube-music MUST come before youtube so music.youtube.com matches first
  'youtube-music': {
    name: 'YouTube Music',
    patterns: [/music\.youtube\.com/],
    icon: 'music',
    color: '#FF0000',
  },
  youtube: {
    name: 'YouTube',
    patterns: [/youtube\.com/, /youtu\.be/],
    icon: 'youtube',
    color: '#FF0000',
  },
  pandora: {
    name: 'Pandora',
    patterns: [/pandora\.com/],
    icon: 'music',
    color: '#224099',
  },
  iheartradio: {
    name: 'iHeartRadio',
    patterns: [/iheart\.com/],
    icon: 'radio',
    color: '#C6002B',
  },
  shazam: {
    name: 'Shazam',
    patterns: [/shazam\.com/],
    icon: 'music',
    color: '#0088FF',
  },
  tidal: {
    name: 'Tidal',
    patterns: [/tidal\.com/, /listen\.tidal\.com/],
    icon: 'music',
    color: '#000000',
  },

  // Video
  tiktok: {
    name: 'TikTok',
    patterns: [/tiktok\.com/, /vm\.tiktok\.com/],
    icon: 'video',
    color: '#000000',
  },
  twitch: {
    name: 'Twitch',
    patterns: [/twitch\.tv/],
    icon: 'twitch',
    color: '#9146FF',
  },
  kick: {
    name: 'Kick',
    patterns: [/kick\.com/],
    icon: 'video',
    color: '#53FC18',
  },
  vimeo: {
    name: 'Vimeo',
    patterns: [/vimeo\.com/],
    icon: 'video',
    color: '#1AB7EA',
  },

  // Marketplaces
  amazon: {
    name: 'Amazon',
    patterns: [/amazon\.com/, /amazon\.fr/, /amazon\.de/, /amazon\.co\.uk/, /amzn\.to/, /amzn\.eu/],
    icon: 'shopping-cart',
    color: '#FF9900',
  },
  etsy: {
    name: 'Etsy',
    patterns: [/etsy\.com/],
    icon: 'shopping-bag',
    color: '#F56400',
  },
  ebay: {
    name: 'eBay',
    patterns: [/ebay\.com/, /ebay\.fr/, /ebay\.de/, /ebay\.co\.uk/],
    icon: 'shopping-cart',
    color: '#E53238',
  },
  walmart: {
    name: 'Walmart',
    patterns: [/walmart\.com/],
    icon: 'shopping-cart',
    color: '#0071DC',
  },
  vinted: {
    name: 'Vinted',
    patterns: [/vinted\.com/, /vinted\.fr/, /vinted\.de/],
    icon: 'shirt',
    color: '#09B1BA',
  },
  booking: {
    name: 'Booking.com',
    patterns: [/booking\.com/],
    icon: 'bed',
    color: '#003580',
  },
  target: {
    name: 'Target',
    patterns: [/target\.com/],
    icon: 'shopping-cart',
    color: '#CC0000',
  },
  staples: {
    name: 'Staples',
    patterns: [/staples\.com/],
    icon: 'shopping-cart',
    color: '#CC0000',
  },
  'home-depot': {
    name: 'Home Depot',
    patterns: [/homedepot\.com/],
    icon: 'shopping-cart',
    color: '#F96302',
  },
  aliexpress: {
    name: 'AliExpress',
    patterns: [/aliexpress\.com/, /s\.click\.aliexpress\.com/],
    icon: 'shopping-cart',
    color: '#FF4747',
  },
  'foot-locker': {
    name: 'Foot Locker',
    patterns: [/footlocker\.com/],
    icon: 'shopping-bag',
    color: '#000000',
  },
  gamestop: {
    name: 'GameStop',
    patterns: [/gamestop\.com/],
    icon: 'shopping-cart',
    color: '#E01A2B',
  },
  wayfair: {
    name: 'Wayfair',
    patterns: [/wayfair\.com/],
    icon: 'shopping-cart',
    color: '#7B189F',
  },
  temu: {
    name: 'Temu',
    patterns: [/temu\.com/],
    icon: 'shopping-cart',
    color: '#F97316',
  },
  shopee: {
    name: 'Shopee',
    patterns: [/shopee\.com/, /shopee\.co\.id/, /shopee\.com\.my/, /shopee\.sg/, /shopee\.co\.th/, /shopee\.vn/, /shopee\.ph/, /shopee\.tw/],
    icon: 'shopping-bag',
    color: '#EE4D2D',
  },
  lazada: {
    name: 'Lazada',
    patterns: [/lazada\.com/, /lazada\.co\.id/, /lazada\.com\.my/, /lazada\.sg/, /lazada\.co\.th/, /lazada\.vn/, /lazada\.com\.ph/],
    icon: 'shopping-cart',
    color: '#0F146D',
  },
  shein: {
    name: 'Shein',
    patterns: [/shein\.com/, /shein\.fr/, /shein\.de/, /shein\.co\.uk/],
    icon: 'shopping-bag',
    color: '#000000',
  },
  'mercado-libre': {
    name: 'Mercado Libre',
    patterns: [/mercadolibre\.com/, /mercadolivre\.com\.br/, /mercadolibre\.com\.ar/, /mercadolibre\.com\.mx/],
    icon: 'shopping-cart',
    color: '#FFE600',
  },
  ltk: {
    name: 'LTK',
    patterns: [/liketoknow\.it/, /ltk\.app/],
    icon: 'shopping-bag',
    color: '#000000',
  },
  'best-buy': {
    name: 'Best Buy',
    patterns: [/bestbuy\.com/],
    icon: 'shopping-cart',
    color: '#0046BE',
  },
  ikea: {
    name: 'IKEA',
    patterns: [/ikea\.com/],
    icon: 'shopping-cart',
    color: '#0058A3',
  },

  // Social
  instagram: {
    name: 'Instagram',
    patterns: [/instagram\.com/, /instagr\.am/],
    icon: 'instagram',
    color: '#E4405F',
  },
  facebook: {
    name: 'Facebook',
    patterns: [/facebook\.com/, /fb\.com/, /fb\.me/],
    icon: 'facebook',
    color: '#1877F2',
  },
  linkedin: {
    name: 'LinkedIn',
    patterns: [/linkedin\.com/],
    icon: 'linkedin',
    color: '#0A66C2',
  },
  twitter: {
    name: 'X (Twitter)',
    patterns: [/twitter\.com/, /x\.com/],
    icon: 'twitter',
    color: '#000000',
  },
  snapchat: {
    name: 'Snapchat',
    patterns: [/snapchat\.com/, /t\.snapchat\.com/],
    icon: 'snapchat',
    color: '#FFFC00',
  },
  pinterest: {
    name: 'Pinterest',
    patterns: [/pinterest\.com/, /pin\.it/],
    icon: 'pinterest',
    color: '#BD081C',
  },
  reddit: {
    name: 'Reddit',
    patterns: [/reddit\.com/, /redd\.it/],
    icon: 'reddit',
    color: '#FF4500',
  },
  weibo: {
    name: 'Weibo',
    patterns: [/weibo\.com/, /weibo\.cn/, /m\.weibo\.cn/],
    icon: 'message-circle',
    color: '#DF2029',
  },

  // Messaging
  whatsapp: {
    name: 'WhatsApp',
    patterns: [/wa\.me/, /whatsapp\.com/],
    icon: 'message-circle',
    color: '#25D366',
  },
  messenger: {
    name: 'Messenger',
    patterns: [/messenger\.com/, /m\.me/],
    icon: 'message-circle',
    color: '#006AFF',
  },
  telegram: {
    name: 'Telegram',
    patterns: [/t\.me/, /telegram\.me/, /telegram\.org/],
    icon: 'send',
    color: '#26A5E4',
  },
  wechat: {
    name: 'WeChat',
    patterns: [/weixin\.qq\.com/, /wechat\.com/],
    icon: 'message-circle',
    color: '#07C160',
  },
  qq: {
    name: 'QQ',
    patterns: [/im\.qq\.com/],
    icon: 'message-circle',
    color: '#EB1923',
  },

  // Tools
  'google-maps': {
    name: 'Google Maps',
    patterns: [/maps\.google\.com/, /goo\.gl\/maps/, /maps\.app\.goo\.gl/],
    icon: 'map-pin',
    color: '#4285F4',
  },
  'google-docs': {
    name: 'Google Docs',
    patterns: [/docs\.google\.com\/document/],
    icon: 'file-text',
    color: '#4285F4',
  },
  'google-sheets': {
    name: 'Google Sheets',
    patterns: [/docs\.google\.com\/spreadsheets/],
    icon: 'table',
    color: '#0F9D58',
  },
  notion: {
    name: 'Notion',
    patterns: [/notion\.so/, /notion\.site/],
    icon: 'book-open',
    color: '#000000',
  },
  slack: {
    name: 'Slack',
    patterns: [/slack\.com/],
    icon: 'hash',
    color: '#4A154B',
  },

  // Podcasts
  'pocket-casts': {
    name: 'Pocket Casts',
    patterns: [/pocketcasts\.com/],
    icon: 'mic',
    color: '#F43E37',
  },
  overcast: {
    name: 'Overcast',
    patterns: [/overcast\.fm/],
    icon: 'mic',
    color: '#FC7E0F',
  },
  castro: {
    name: 'Castro',
    patterns: [/castro\.fm/],
    icon: 'mic',
    color: '#00B265',
  },
  castbox: {
    name: 'Castbox',
    patterns: [/castbox\.fm/],
    icon: 'mic',
    color: '#F55B23',
  },
  stitcher: {
    name: 'Stitcher',
    patterns: [/stitcher\.com/],
    icon: 'mic',
    color: '#000000',
  },

  // Events
  meetup: {
    name: 'Meetup',
    patterns: [/meetup\.com/],
    icon: 'calendar',
    color: '#F65858',
  },
  eventbrite: {
    name: 'Eventbrite',
    patterns: [/eventbrite\.com/, /eventbrite\.fr/, /eventbrite\.de/, /eventbrite\.co\.uk/],
    icon: 'calendar',
    color: '#F05537',
  },
  axs: {
    name: 'AXS',
    patterns: [/axs\.com/],
    icon: 'ticket',
    color: '#1A1A2E',
  },
  ticketmaster: {
    name: 'Ticketmaster',
    patterns: [/ticketmaster\.com/, /ticketmaster\.fr/, /ticketmaster\.de/, /ticketmaster\.co\.uk/],
    icon: 'ticket',
    color: '#026CDF',
  },

  // Music (new)
  audiomack: {
    name: 'Audiomack',
    patterns: [/audiomack\.com/],
    icon: 'music',
    color: '#FFA200',
  },
  beatstars: {
    name: 'BeatStars',
    patterns: [/beatstars\.com/],
    icon: 'music',
    color: '#FF0050',
  },
  beatport: {
    name: 'Beatport',
    patterns: [/beatport\.com/],
    icon: 'music',
    color: '#94D500',
  },
  linkfire: {
    name: 'Linkfire',
    patterns: [/lnk\.to/, /linkfire\.com/],
    icon: 'link',
    color: '#FF4F00',
  },
  mixcloud: {
    name: 'Mixcloud',
    patterns: [/mixcloud\.com/],
    icon: 'music',
    color: '#5000FF',
  },
  tunein: {
    name: 'TuneIn',
    patterns: [/tunein\.com/],
    icon: 'radio',
    color: '#14D8CC',
  },

  // Video (new)
  kickstarter: {
    name: 'Kickstarter',
    patterns: [/kickstarter\.com/],
    icon: 'film',
    color: '#05CE78',
  },

  // Podcasts (new)
  acast: {
    name: 'Acast',
    patterns: [/acast\.com/, /shows\.acast\.com/],
    icon: 'mic',
    color: '#2B2B2B',
  },

  // Tour Dates
  bandsintown: {
    name: 'Bandsintown',
    patterns: [/bandsintown\.com/],
    icon: 'calendar',
    color: '#00CEC8',
  },
  seated: {
    name: 'Seated',
    patterns: [/seated\.com/],
    icon: 'ticket',
    color: '#000000',
  },
  songkick: {
    name: 'Songkick',
    patterns: [/songkick\.com/],
    icon: 'calendar',
    color: '#F80046',
  },

  // Social (new)
  strava: {
    name: 'Strava',
    patterns: [/strava\.com/],
    icon: 'activity',
    color: '#FC4C02',
  },
  threads: {
    name: 'Threads',
    patterns: [/threads\.net/],
    icon: 'at-sign',
    color: '#000000',
  },

  // Other (new)
  airbnb: {
    name: 'Airbnb',
    patterns: [/airbnb\.com/, /airbnb\.fr/, /airbnb\.de/, /airbnb\.co\.uk/, /airbnb\.es/, /airbnb\.it/],
    icon: 'home',
    color: '#FF5A5F',
  },
  behance: {
    name: 'Behance',
    patterns: [/behance\.net/],
    icon: 'pen-tool',
    color: '#1769FF',
  },
  calendly: {
    name: 'Calendly',
    patterns: [/calendly\.com/],
    icon: 'calendar',
    color: '#006BFF',
  },
  gofundme: {
    name: 'GoFundMe',
    patterns: [/gofundme\.com/],
    icon: 'heart',
    color: '#00B964',
  },
  imgur: {
    name: 'Imgur',
    patterns: [/imgur\.com/],
    icon: 'image',
    color: '#1BB76E',
  },
  laylo: {
    name: 'Laylo',
    patterns: [/laylo\.com/],
    icon: 'bell',
    color: '#000000',
  },
  matterport: {
    name: 'Matterport',
    patterns: [/matterport\.com/, /my\.matterport\.com/],
    icon: 'box',
    color: '#FF3366',
  },
  shopmy: {
    name: 'ShopMy',
    patterns: [/shopmy\.us/],
    icon: 'shopping-bag',
    color: '#000000',
  },
  substack: {
    name: 'Substack',
    patterns: [/substack\.com/],
    icon: 'mail',
    color: '#FF6719',
  },
  typeform: {
    name: 'Typeform',
    patterns: [/typeform\.com/],
    icon: 'clipboard',
    color: '#262627',
  },

  // Generic fallback (must be last)
  generic: {
    name: 'Link',
    patterns: [],
    icon: 'link',
    color: '#6B7280',
  },
};

export const detectPlatform = (url: string): Platform => {
  const normalizedUrl = url.toLowerCase();

  for (const [platform, config] of Object.entries(platformConfigs)) {
    if (platform === 'generic') continue;

    for (const pattern of config.patterns) {
      if (pattern.test(normalizedUrl)) {
        return platform as Platform;
      }
    }
  }

  return 'generic';
};

export const getPlatformConfig = (platform: Platform): PlatformConfig => {
  return platformConfigs[platform] || platformConfigs.generic;
};

export const getPlatformName = (platform: Platform): string => {
  return getPlatformConfig(platform).name;
};

export const getPlatformColor = (platform: Platform): string => {
  return getPlatformConfig(platform).color;
};

export const MUSIC_PLATFORMS: Set<Platform> = new Set([
  'spotify',
  'apple-music',
  'deezer',
  'soundcloud',
  'amazon-music',
  'youtube-music',
  'tidal',
  'pandora',
  'audiomack',
]);

export const isMusicPlatform = (platform: Platform): boolean => {
  return MUSIC_PLATFORMS.has(platform);
};
